import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import LoginPage from './components/LoginPage/LoginPage';
import RegisterPage from './components/RegisterPage/RegisterPage';
import AdminLayout from './layouts/AdminLayout';
import './App.css';
import UserManager from './components/UserManager/UserManager';
import PermissionManager from './components/PermissionManager/PermissionManager';
import VideoBrowser from './components/VideoBrowser/VideoBrowser';
import ImageBrowser from './components/ImageBrowser/ImageBrowser';
import VideoPlayPage from './components/VideoBrowser/VideoPlayPage';
import FundBrowser from './components/FundBrowser/FundBrowser';
import FundDetailPage from './components/FundBrowser/FundDetailPage';
import NewsBrowser from './components/NewsBrowser/NewsBrowser';
import FundAgentInbox from './components/FundAgentInbox/FundAgentInbox';
import RequireUserManager from './components/auth/RequireUserManager';
import RequireSuperAdmin from './components/auth/RequireSuperAdmin';
import {
    isAuthenticated,
    clearAuth,
    migrateLegacyAuth,
    getUser,
    syncAuthState,
} from './utils/auth';
import { canManageUsers, isNormalUser } from './utils/role';

migrateLegacyAuth();

/** 登录后默认落地页 */
const getDefaultAdminPath = () => {
    const user = getUser();
    if (canManageUsers(user)) return '/admin/users';
    if (isNormalUser(user)) return '/admin/videos';
    return '/admin/videos';
};

class App extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            isAuthenticated: isAuthenticated(),
        };
    }

    componentDidMount() {
        this.handleAuthSync = () => {
            const authed = syncAuthState();
            if (this.state.isAuthenticated !== authed) {
                this.setState({ isAuthenticated: authed });
            }
        };
        document.addEventListener('visibilitychange', this.handleAuthSync);
        window.addEventListener('focus', this.handleAuthSync);
    }

    componentWillUnmount() {
        document.removeEventListener('visibilitychange', this.handleAuthSync);
        window.removeEventListener('focus', this.handleAuthSync);
    }

    handleLogin = (callback) => {
        this.setState({ isAuthenticated: isAuthenticated() }, () => {
            if (typeof callback === 'function') {
                callback();
            }
        });
    };

    /** 始终以实时校验为准，避免 state 缓存导致过期仍视为已登录 */
    isAuthed = () => isAuthenticated();

    handleLogout = () => {
        clearAuth();
        this.setState({ isAuthenticated: false }, () => {
            window.location.href = '/login';
        });
    };

    render() {
        const authed = this.isAuthed();
        const defaultPath = getDefaultAdminPath();

        return (
            <BrowserRouter>
                <div className="app-container">
                    <Routes>
                        <Route
                            path="/login"
                            element={
                                !authed ? (
                                    <LoginPage onLogin={this.handleLogin} />
                                ) : (
                                    <Navigate to={defaultPath} replace />
                                )
                            }
                        />
                        <Route
                            path="/play/video/:videoId"
                            element={
                                authed ? (
                                    <VideoPlayPage />
                                ) : (
                                    <Navigate to="/login" replace />
                                )
                            }
                        />
                        <Route
                            path="/register"
                            element={
                                !authed ? (
                                    <RegisterPage />
                                ) : (
                                    <Navigate to={defaultPath} replace />
                                )
                            }
                        />
                        <Route
                            path="/admin"
                            element={
                                authed ? (
                                    <AdminLayout onLogout={this.handleLogout} />
                                ) : (
                                    <Navigate to="/login" replace />
                                )
                            }
                        >
                            <Route
                                index
                                element={<Navigate to={defaultPath.replace('/admin/', '')} replace />}
                            />
                            <Route
                                path="users"
                                element={
                                    <RequireUserManager>
                                        <UserManager />
                                    </RequireUserManager>
                                }
                            />
                            <Route path="videos" element={<VideoBrowser />} />
                            <Route path="images" element={<ImageBrowser />} />
                            <Route path="funds" element={<FundBrowser />} />
                            <Route path="funds/:code" element={<FundDetailPage />} />
                            <Route path="news" element={<NewsBrowser />} />
                            <Route path="agent-inbox" element={<FundAgentInbox />} />
                            <Route
                                path="permissions"
                                element={
                                    <RequireSuperAdmin>
                                        <PermissionManager />
                                    </RequireSuperAdmin>
                                }
                            />
                            <Route path="*" element={<Navigate to={defaultPath.replace('/admin/', '')} replace />} />
                        </Route>
                        <Route
                            path="/"
                            element={
                                <Navigate to={authed ? defaultPath : '/login'} replace />
                            }
                        />
                        <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                </div>
            </BrowserRouter>
        );
    }
}

export default App;
